import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { FileSortOrder } from '@/types/node';
import { UndoRedoEntry } from '../undoRedo';
import type { BaseNodeData, NodeViewPreferences } from '@/types/node';
import type { FileKind, NodeId, Web, Edit } from 'web';
import type { FlowGraph } from '../FlowGraph';
import type { ClearableAssetsView } from './NodeAssetManager';

/**
 * Base class for view classes that manage file lists and selected index.
 * Provides common functionality for adding, removing, and managing files.
 */
export abstract class BaseFileView<
  TNodeData extends BaseNodeData & NodeViewPreferences & { selectedIndex: number },
> implements ClearableAssetsView {
  constructor(
    protected graph: FlowGraph,
    protected nodeId: NodeId,
  ) {}

  /**
   * Get the node data. Must be implemented by subclasses.
   */
  abstract getNodeData(): TNodeData;

  /**
   * Get the list of files. Must be implemented by subclasses as the source differs.
   */
  abstract getFilesList(): FileKind[];

  /**
   * Set files in the WASM layer. Implementation differs between subclasses.
   */
  protected abstract wasmSetFiles(wasm: Web, files: FileKind[]): Edit;

  protected ensureInsertionOrders(files: FileKind[]): FileKind[] {
    if (files.every((f) => f.insertionOrder !== undefined)) return files;

    return files.map((file, index) => ({ ...file, insertionOrder: index }));
  }

  private getNextInsertionOrder(files: FileKind[]): number {
    return files.reduce((max, f) => {
      const order: number = f.insertionOrder ?? 0;
      return Math.max(max, order + 1);
    }, 0);
  }

  protected sortFilesByName(files: FileKind[]): FileKind[] {
    const unnamed = files.filter((f) => !f.name);
    const named = files.filter((f) => f.name);
    named.sort((a, b) => (a.name as string).localeCompare(b.name as string, undefined, { numeric: true }));

    return [...unnamed, ...named];
  }

  private sortFilesByDateAdded(files: FileKind[]): FileKind[] {
    const sorted = [...files];
    sorted.sort((a, b) => (a.insertionOrder ?? 0) - (b.insertionOrder ?? 0));

    return sorted;
  }

  addFile(file: FileKind, ongoing = false) {
    if (this.graph.store.getNodeNullable(this.nodeId) === undefined) {
      return;
    }
    const existingFiles = this.ensureInsertionOrders(this.getFilesList());
    const newFile: FileKind = { ...file, insertionOrder: this.getNextInsertionOrder(existingFiles) };
    const newFiles = [...existingFiles, newFile];

    const sortOrder = this.getNodeData().sortOrder;
    if (sortOrder === FileSortOrder.Name) {
      const sorted = this.sortFilesByName(newFiles);
      const newSelectedIndex = sorted.findIndex((f) => f === newFile);
      this.graph.edit(() => this.updateFiles(sorted, newSelectedIndex), ongoing);
    } else {
      this.graph.edit(() => this.updateFiles(newFiles, existingFiles.length), ongoing);
    }
    this.graph.updateNodeOutputs(this.nodeId, ongoing);
    this.graph.loadResources();
  }

  removeFile(indexToRemove: number, ongoing = false) {
    const filesList = this.getFilesList();
    if (!filesList[indexToRemove]) throw new Error('invalid index');
    let newSelectedIndex = indexToRemove;
    // if the selected index is the last index, set the selected index to one before the last index
    if (newSelectedIndex === filesList.length - 1 && filesList.length > 1) {
      newSelectedIndex--;
    }
    const newFiles = filesList.filter((_, index) => index !== indexToRemove);
    this.graph.edit(() => this.updateFiles(newFiles, newSelectedIndex), ongoing);
    this.graph.updateNodeOutputs(this.nodeId, ongoing);
    this.graph.loadResources();
  }

  getSelectedIndex(): number {
    return this.getNodeData().selectedIndex;
  }

  deleteCurrent() {
    this.removeFile(this.getSelectedIndex());
  }

  deleteAllOthers() {
    const files = this.getFilesList();
    const selectedIndex = this.getSelectedIndex();
    const newFiles = files.filter((_, index) => index === selectedIndex);
    this.setFiles(newFiles, 0, false);
  }

  deleteAll() {
    this.setFiles([], 0, false);
  }

  /**
   * Creates an UndoRedoEntry for updating files (core implementation shared by setFiles and clearAllAssets)
   */
  private updateFiles(files: FileKind[], selectedIndex: number | undefined): UndoRedoEntry {
    this.clearPastedAndInitialData();
    const undoRedoEntry = UndoRedoEntry.empty();
    undoRedoEntry.add(this.graph.wasmChange((wasm) => this.wasmSetFiles(wasm, files)));
    if (selectedIndex !== undefined) {
      undoRedoEntry.add(this.graph.wasmChange((wasm) => wasm.setSelectedMedia(this.nodeId, selectedIndex)));
    }
    return undoRedoEntry;
  }

  /**
   * Clears all assets from this view (returns UndoRedoEntry for batching with other operations)
   */
  clearAllAssets(): UndoRedoEntry {
    return this.updateFiles([], 0);
  }

  /**
   * Checks if this view has any assets
   */
  hasAssets(): boolean {
    return this.getFilesList().length > 0;
  }

  /**
   * Clears all assets except the currently selected one (returns UndoRedoEntry for batching)
   */
  clearOtherAssets(): UndoRedoEntry {
    const files = this.getFilesList();
    const selectedIndex = this.getSelectedIndex();
    const newFiles = files.filter((_, index) => index === selectedIndex);
    return this.updateFiles(newFiles, 0);
  }

  hasMultipleAssets(): boolean {
    return this.getFilesList().length > 1;
  }

  setFiles(files: FileKind[], selectedIndex: number | undefined, ongoing: boolean) {
    this.graph.edit(() => this.updateFiles(files, selectedIndex), ongoing);
    this.graph.updateNodeOutputs(this.nodeId, ongoing);
    this.graph.loadResources();
  }

  setSelectedIndex(selectedIndex: number | undefined, ongoing: boolean) {
    this.graph.wasmEdit((wasm) => {
      const filesLength = this.getFilesList().length;
      return wasm.setSelectedMedia(this.nodeId, selectedIndex ?? (filesLength > 0 ? filesLength - 1 : 0));
    }, ongoing);
    this.graph.updateNodeOutputs(this.nodeId, ongoing);
    this.graph.loadImageResources();
  }

  clearPastedAndInitialData() {
    this.graph.store.updateNodeData<TNodeData>(
      this.nodeId,
      (_) =>
        ({
          pastedData: undefined,
          initialData: undefined,
        }) as Partial<TNodeData>,
    );
  }

  setViewMode(viewMode: NodeViewMode) {
    this.graph.store.updateNodeData<TNodeData>(
      this.nodeId,
      (_) =>
        ({
          viewMode,
        }) as Partial<TNodeData>,
    );
  }

  setSortOrder(sortOrder: FileSortOrder) {
    const files = this.ensureInsertionOrders(this.getFilesList());
    const selectedFile = files[this.getSelectedIndex()];
    const sortedFiles =
      sortOrder === FileSortOrder.Name ? this.sortFilesByName(files) : this.sortFilesByDateAdded(files);
    const newSelectedIndex = selectedFile ? sortedFiles.indexOf(selectedFile) : 0;

    this.graph.edit(() => {
      const entry = this.updateFiles(sortedFiles, newSelectedIndex);
      entry.add(this.graph.store.updateNodeData<TNodeData>(this.nodeId, (_) => ({ sortOrder }) as Partial<TNodeData>));
      return entry;
    }, false);
    this.graph.updateNodeOutputs(this.nodeId, false);
    this.graph.loadResources();
  }
}
